import type { BufferPoolItem } from './vtkManagers';

export type TaviParsedContour = {
  centerIdx: number;
  pointCount: number;
  labelPoints: Array<[number, number]>;
  areaSize: number;
  areaPerimeter: number;
  minDiameter: number;
  maxDiameter: number;
  extraPointCount?: number;
  extraPoints?: Array<[number, number]>;
};

export type TaviParsedData = {
  centerline?: Float64Array | null;
  centerTangent?: Float64Array | null;
  center_dx?: Float64Array | null;
  center_dy?: Float64Array | null;
  annulus?: TaviParsedContour | null;
  sinus?: TaviParsedContour | null;
  sinotubularjunction?: TaviParsedContour | null;
  cusp?: {
    right: { point: [number, number, number]; metric: number; height?: number } | null;
    left: { point: [number, number, number]; metric: number; height?: number } | null;
    non: { point: [number, number, number]; metric: number; height?: number } | null;
  } | null;
  ostium?: {
    lca: {
      point: [number, number, number];
      metric: number;
      length?: number;
    } | null;
    rca: {
      point: [number, number, number];
      metric: number;
      length?: number;
    } | null;
  } | null;
  measurements?: {
    RCC_len: number;
    LCC_len: number;
    NCC_len: number;
    STJ_height: number;
  } | null;
  heightPlane?:
    | {
        rca: {
          center: [number, number, number];
          normal: [number, number, number];
          dx: [number, number, number];
          dy: [number, number, number];
          ostium2d: [number, number];
          projected2d: [number, number];
          cusp2d: [number, number];
        };
        lca: {
          center: [number, number, number];
          normal: [number, number, number];
          dx: [number, number, number];
          dy: [number, number, number];
          ostium2d: [number, number];
          projected2d: [number, number];
          cusp2d: [number, number];
        };
        stj: {
          center: [number, number, number];
          normal: [number, number, number];
          dx: [number, number, number];
          dy: [number, number, number];
          ostium2d: [number, number];
          projected2d: [number, number];
          cusp2d: [number, number];
        };
      }
    | null;
};

export function toArrayBuffer(data: unknown): ArrayBuffer | null {
  if (!data) return null;
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) {
    const v = data as ArrayBufferView;
    return v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength);
  }
  return null;
}

export function parseTaviContour(buffer: ArrayBuffer): TaviParsedContour | null {
  if (buffer.byteLength < 8) return null;
  const view = new DataView(buffer);
  const centerIdx = view.getInt32(0, true);
  const pointCount = view.getInt32(4, true);
  const metricOffset = 8 + pointCount * 8;
  if (metricOffset + 32 > buffer.byteLength) return null;

  const labelPoints: Array<[number, number]> = [];
  let off = 8;
  for (let i = 0; i < pointCount; i++) {
    labelPoints.push([view.getInt32(off, true), view.getInt32(off + 4, true)]);
    off += 8;
  }
  const areaSize = view.getFloat64(metricOffset, true);
  const areaPerimeter = view.getFloat64(metricOffset + 8, true);
  const minDiameter = view.getFloat64(metricOffset + 16, true);
  const maxDiameter = view.getFloat64(metricOffset + 24, true);
  let extraPointCount = 0;
  const extraPoints: Array<[number, number]> = [];
  off = metricOffset + 32;
  if (off + 4 <= buffer.byteLength) {
    extraPointCount = view.getInt32(off, true);
    off += 4;
    for (let i = 0; i < extraPointCount; i++) {
      if (off + 16 > buffer.byteLength) break;
      extraPoints.push([view.getFloat64(off, true), view.getFloat64(off + 8, true)]);
      off += 16;
    }
  }

  return {
    centerIdx,
    pointCount,
    labelPoints,
    areaSize,
    areaPerimeter,
    minDiameter,
    maxDiameter,
    extraPointCount,
    extraPoints,
  };
}

export function parseTaviBufferByName(name: string, data: unknown): unknown {
  const buffer = toArrayBuffer(data);
  if (!buffer) return null;

  if (
    name === 'centerline' ||
    name === 'centerTangent' ||
    name === 'center_dx' ||
    name === 'center_dy'
  ) {
    return new Float64Array(buffer);
  }
  if (name === 'annulus' || name === 'sinus' || name === 'sinotubularjunction') {
    return parseTaviContour(buffer);
  }
  if (name === 'ostium') {
    const view = new DataView(buffer);
    const rows: Array<{ point: [number, number, number]; metric: number }> = [];
    for (let off = 0; off + 32 <= view.byteLength; off += 32) {
      rows.push({
        point: [
          view.getFloat64(off, true),
          view.getFloat64(off + 8, true),
          view.getFloat64(off + 16, true),
        ],
        metric: view.getFloat64(off + 24, true),
      });
    }
    return {
      lca: rows[0] ? { ...rows[0], length: rows[0].metric } : null,
      rca: rows[1] ? { ...rows[1], length: rows[1].metric } : null,
    };
  }
  if (name === 'cusp') {
    const view = new DataView(buffer);
    const rows: Array<{ point: [number, number, number]; metric: number }> = [];
    for (let off = 0; off + 32 <= view.byteLength; off += 32) {
      rows.push({
        point: [
          view.getFloat64(off, true),
          view.getFloat64(off + 8, true),
          view.getFloat64(off + 16, true),
        ],
        metric: view.getFloat64(off + 24, true),
      });
    }
    return {
      right: rows[0] ? { ...rows[0], height: rows[0].metric } : null,
      left: rows[1] ? { ...rows[1], height: rows[1].metric } : null,
      non: rows[2] ? { ...rows[2], height: rows[2].metric } : null,
    };
  }
  if (name === 'measurements') {
    if (buffer.byteLength < 32) return null;
    const v = new Float64Array(buffer);
    return {
      RCC_len: v[0],
      LCC_len: v[1],
      NCC_len: v[2],
      STJ_height: v[3],
    };
  }
  if (name === 'heightPlane') {
    const view = new DataView(buffer);
    const rowSize = 18 * 8;
    if (view.byteLength < rowSize * 3) return null;
    const parseRow = (base: number) => ({
      center: [
        view.getFloat64(base, true),
        view.getFloat64(base + 8, true),
        view.getFloat64(base + 16, true),
      ] as [number, number, number],
      normal: [
        view.getFloat64(base + 24, true),
        view.getFloat64(base + 32, true),
        view.getFloat64(base + 40, true),
      ] as [number, number, number],
      dx: [
        view.getFloat64(base + 48, true),
        view.getFloat64(base + 56, true),
        view.getFloat64(base + 64, true),
      ] as [number, number, number],
      dy: [
        view.getFloat64(base + 72, true),
        view.getFloat64(base + 80, true),
        view.getFloat64(base + 88, true),
      ] as [number, number, number],
      ostium2d: [
        view.getFloat64(base + 96, true),
        view.getFloat64(base + 104, true),
      ] as [number, number],
      projected2d: [
        view.getFloat64(base + 112, true),
        view.getFloat64(base + 120, true),
      ] as [number, number],
      cusp2d: [
        view.getFloat64(base + 128, true),
        view.getFloat64(base + 136, true),
      ] as [number, number],
    });
    return {
      rca: parseRow(0),
      lca: parseRow(rowSize),
      stj: parseRow(rowSize * 2),
    };
  }
  return buffer;
}

export function getTaviParsedData(pool?: BufferPoolItem): TaviParsedData | null {
  return ((pool as Record<string, any> | undefined)?.['tavi.parsed']?.data ??
    null) as TaviParsedData | null;
}

export function getTaviCenterIdx(
  pool: BufferPoolItem | undefined,
  key: 'annulus' | 'sinus' | 'sinotubularjunction',
): number | null {
  const parsed = getTaviParsedData(pool);
  const fromParsed = parsed?.[key]?.centerIdx;
  if (typeof fromParsed === 'number' && fromParsed >= 0) return fromParsed;

  const raw = (pool as Record<string, any> | undefined)?.[`tavi.${key}`]?.data;
  const buffer = toArrayBuffer(raw);
  if (!buffer || buffer.byteLength < 4) return null;
  const idx = new DataView(buffer).getInt32(0, true);
  return idx >= 0 ? idx : null;
}
